import type { FC } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { aboutInfo } from '@/mocks/data';

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

const AboutPage: FC = () => {
  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <SchemaOrg
        type="AboutPage"
        data={{
          name: '关于我们 - 17c.com',
          url: 'https://www.17c.com/about',
          description: aboutInfo.description,
          inLanguage: 'zh-Hans',
          about: {
            '@type': 'Organization',
            name: '17c.com',
            description: aboutInfo.description,
          },
        }}
      />
      <SchemaOrg
        type="Organization"
        data={{
          name: '17c.com',
          url: 'https://www.17c.com/',
          description: aboutInfo.description,
          email: aboutInfo.contact.email,
          telephone: aboutInfo.contact.phone,
          address: {
            '@type': 'PostalAddress',
            streetAddress: aboutInfo.contact.address,
            addressCountry: 'CN',
          },
        }}
      />

      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb items={breadcrumbItems} />
          <h1 className="mt-4 text-3xl md:text-4xl font-bold text-foreground-950 font-heading">
            关于 17c.com
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl">
            {aboutInfo.slogan}
          </p>
        </div>
      </div>

      <section className="py-14 md:py-18 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10 md:gap-14 items-center">
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                品牌介绍
              </h2>
              <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed">
                {aboutInfo.description}
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                {aboutInfo.mission}
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-50 text-accent-600">
                    <i className="ri-check-line" />
                  </div>
                  <span className="text-sm text-foreground-700">ISO 27001 认证</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-50 text-accent-600">
                    <i className="ri-check-line" />
                  </div>
                  <span className="text-sm text-foreground-700">等保三级认证</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-50 text-accent-600">
                    <i className="ri-check-line" />
                  </div>
                  <span className="text-sm text-foreground-700">SOC 2 Type II</span>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 w-full lg:w-[420px] h-[280px] md:h-[320px] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20office%20workspace%20with%20warm%20lighting%20and%20minimalist%20design%2C%20collaborative%20team%20environment%2C%20clean%20aesthetic%20with%20wood%20accents%20and%20plants%2C%20creative%20technology%20company%20atmosphere%2C%20professional%20editorial%20photography%20with%20natural%20light&width=840&height=640&seq=about-brand-17c&orientation=landscape"
                alt="17c.com 团队工作环境"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-18 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              核心价值观
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              驱动我们不断前行的信念
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {aboutInfo.values.map((v, i) => (
              <div key={i} className="p-6 md:p-7 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                  <span className="text-lg font-bold font-heading">{i + 1}</span>
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground-950 font-label">{v.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-18 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              发展历程
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              从创立至今的成长足迹
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2" />
              <div className="space-y-8">
                {aboutInfo.milestones.map((m, i) => (
                  <div key={i} className={`relative flex items-start gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="hidden md:block flex-1" />
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 -translate-x-1/2 mt-1.5 z-10 ring-4 ring-accent-100" />
                    <div className="ml-10 md:ml-0 md:flex-1 p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70">
                      <span className="text-sm font-bold text-accent-600 font-heading">{m.year}</span>
                      <p className="mt-1 text-sm text-foreground-700 leading-relaxed">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-18 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              联系我们
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              欢迎随时与我们取得联系，我们将竭诚为您服务
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 max-w-4xl mx-auto">
            <div className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
              <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className="ri-mail-line text-xl" />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-foreground-950 font-label">电子邮箱</h3>
              <a href={`mailto:${aboutInfo.contact.email}`} className="mt-1 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer break-all">
                {aboutInfo.contact.email}
              </a>
            </div>
            <div className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
              <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className="ri-phone-line text-xl" />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-foreground-950 font-label">客服电话</h3>
              <p className="mt-1 text-sm text-foreground-700">{aboutInfo.contact.phone}</p>
            </div>
            <div className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
              <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className="ri-map-pin-line text-xl" />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-foreground-950 font-label">公司地址</h3>
              <p className="mt-1 text-sm text-foreground-700">{aboutInfo.contact.address}</p>
            </div>
            <div className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
              <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className="ri-time-line text-xl" />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-foreground-950 font-label">工作时间</h3>
              <p className="mt-1 text-sm text-foreground-700">{aboutInfo.contact.workHours}</p>
            </div>
          </div>
          <div className="mt-8 max-w-md mx-auto">
            <Link
              to="/category/faq"
              className="block w-full text-center px-6 py-3.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看常见问题
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;